import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { Application } from '@pickleballinc/lib/types';
import { useEffect, useRef, useState } from 'react';

import { SwitcherOptionsInterface } from '../utils/nav.config';

interface HeaderLogoProps {
	application: Application;
	cdnUrl: string;
	isOpen: boolean;
	switcherOptions: SwitcherOptionsInterface[];
	headerHeight: number;
}

const getClassNames = (option: Pick<SwitcherOptionsInterface, 'bg' | 'bgDark'>, isHovered: boolean, isFirstElement: boolean) => {
	const bgClass = isHovered ? option.bg : option.bgDark;

	return {
		outer: `flex items-center max-h-14 pr-2 transition-colors duration-300 ease-in-out ${bgClass} ${isFirstElement ? 'rounded-t-2xl' : ''}`,
		inner: `flex flex-row rounded-tl-2xl rounded-bl-2xl transition-colors duration-300 ease-in-out ${bgClass}`
	};
};

export const HeaderSwitcher = ({ application, cdnUrl, isOpen, switcherOptions, headerHeight }: HeaderLogoProps) => {
	const [screenHeight, setScreenHeight] = useState(0);
	const [screenWidth, setScreenWidth] = useState(0);
	const [isHovered, setIsHovered] = useState(false);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

	const dropdownRef = useRef<HTMLDivElement>(null);

	const selectedOption = switcherOptions.find(({ app }) => app === application);
	const { outer, inner } = selectedOption ? getClassNames(selectedOption, isHovered, true) : { outer: '', inner: '' };
	const otherOptions: SwitcherOptionsInterface[] = switcherOptions.filter(({ app }) => app !== application);

	const lastElement = otherOptions.length - 1;

	const dropdownHeight = otherOptions.length * 56;

	useEffect(() => {
		const screenWidth = document.documentElement.clientWidth;
		setScreenHeight(document.documentElement.clientHeight || otherOptions.length * 56);
		setScreenWidth(document.documentElement.clientWidth);
		if (dropdownRef.current) {
			if (isOpen && screenWidth < 640) {
				const scrollY = window.scrollY;
				document.body.style.overflow = 'hidden';
				document.body.style.position = 'fixed';
				document.body.style.top = `-${scrollY}px`;
				document.body.style.width = '100%';
			} else {
				document.body.style.overflow = '';
				document.body.style.position = '';
				document.body.style.top = '';
				document.body.style.width = '';
			}
		}

		return () => {
			document.body.style.overflow = '';
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.width = '';
		};
	}, [isOpen]);

	return (
		<div
			ref={dropdownRef}
			className="absolute bg-black min-w-[300px] min-[350px]:min-w-[343px] max-w-[343px] !w-full w-grow text-white rounded-2xl shadow-lg overflow-y-auto"
			style={{
				maxHeight: `${screenWidth < 640 ? screenHeight - headerHeight - 20 : dropdownHeight}px`,
				overflowY: screenWidth < 640 ? 'auto' : 'visible'
			}}
		>
			{selectedOption ? (
				<a
					key={selectedOption.label}
					href={selectedOption.link}
					className={`${outer} hover:cursor-pointer`}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
				>
					<div className={inner}>
						<div
							className={`!w-[112px] !min-w-[112px] h-14 flex items-center rounded-br-2xl justify-center ${selectedOption.bg} rounded-tl-2xl`}
							style={{
								clipPath:
									'polygon(100% 0%, 90% 90%, 89.5% 91.5%, 89% 93%, 88% 94.5%, 87% 96%, 86.5% 97.5%, 85% 98.8%, 83% 99.5%, 81% 99.8%, 78% 100%, 0% 100%, 0% 0%)'
							}}
						>
							<img
								src={getCDNURL(`${cdnUrl}/${selectedOption.img}`, 32, 32)}
								alt={selectedOption.label}
								className="h-[30px] mr-[-4px]"
							/>
						</div>
					</div>
					<span className="ml-2 text-lg font-medium">{selectedOption.label}</span>
				</a>
			) : null}
			{otherOptions.map(({ label, img, bg, bgDark, link }, index) => {
				const isItemHovered = hoveredIndex === index;
				const { outer, inner } = getClassNames({ bg, bgDark }, isItemHovered, false);
				return (
					<a
						key={label}
						href={link}
						className={`${outer} hover:cursor-pointer ${index === lastElement ? 'rounded-b-2xl' : ''}`}
						onMouseEnter={() => setHoveredIndex(index)}
						onMouseLeave={() => setHoveredIndex(null)}
					>
						<div className={`${inner}`}>
							<div
								className={`!w-[112px] !min-w-[112px] h-14 flex items-center justify-center ${bg} ${index === lastElement ? 'rounded-bl-2xl' : ''}`}
								style={{
									clipPath:
										'polygon(100% 0%, 90% 90%, 89.5% 91.5%, 89% 93%, 88% 94.5%, 87% 96%, 86.5% 97.5%, 85% 98.8%, 83% 99.5%, 81% 99.8%, 78% 100%, 0% 100%, 0% 0%)'
								}}
							>
								<img src={getCDNURL(`${cdnUrl}/${img}`, 32, 32)} alt={label} className="h-[30px] mr-[-4px]" />
							</div>
						</div>
						<span className="ml-2 text-lg font-medium">{label}</span>
					</a>
				);
			})}
		</div>
	);
};
